import type { OrderDetail, OverviewReceiving, Receipt } from '@/api/orders';
import type { StatusTone } from '@/lib/status';
import { blockerHelp } from '../blockers';
import { setupBreakdown } from '../lib';

/** An issue's state: a problem (`danger`) or something that needs you (`warning`).
 *  Setup debt is `warning` like every other gap. */
export type IssueSeverity = Extract<StatusTone, 'danger' | 'warning'>;

/** One problem on a rail node: a short fact, its severity, the help behind it. */
export interface Issue {
  key: string;
  severity: IssueSeverity;
  text: string;
  help?: string;
}

const RANK: Record<IssueSeverity, number> = { danger: 0, warning: 1 };

export function worstSeverity(issues: Issue[]): IssueSeverity | null {
  if (issues.length === 0) return null;
  return [...issues].sort((a, b) => RANK[a.severity] - RANK[b.severity])[0].severity;
}

/** Setup debt: the shared breakdown, plus each release checked and not found. */
export function setupIssues(detail: OrderDetail): Issue[] {
  const issues: Issue[] = setupBreakdown(detail.counts)
    .filter((p) => p.key !== 'releases')
    .map((p) => ({ key: `setup-${p.key}`, severity: 'warning' as const, text: p.text }));
  const seen = new Set<string>();
  for (const a of detail.announcements) {
    for (const rn of a.releaseNames) {
      if (seen.has(rn.name)) continue;
      seen.add(rn.name);
      if (rn.checked_at && !rn.cat_id) {
        issues.push({ key: `rel-${rn.name}`, severity: 'warning', text: `Release “${rn.name}” not set up` });
      }
    }
  }
  return issues;
}

export function orderedIssues(detail: OrderDetail): Issue[] {
  const issues: Issue[] = [];
  if (detail.orderedCompleteness === 'partial') {
    issues.push({
      key: 'ordered-partial',
      severity: 'warning',
      text: 'Ordered document incomplete',
      help: 'More was ordered than is recorded here. The Notes tab says what is missing.',
    });
  }
  // A journey with setup debt but no announcement carries it here instead.
  if (detail.announcements.length === 0) issues.push(...setupIssues(detail));
  return issues;
}

export function invoicedIssues(detail: OrderDetail): Issue[] {
  const issues: Issue[] = [];
  for (const inv of detail.invoices) {
    const name = inv.invoiceNo || 'Invoice';
    if (inv.total == null) {
      issues.push({ key: `paid-${inv.id}`, severity: 'warning', text: `${name} · amount paid not captured` });
    }
    if (inv.lines.length === 0) {
      issues.push({ key: `lines-${inv.id}`, severity: 'warning', text: `${name} · no product detail` });
    }
    if (inv.sourceConflict) {
      issues.push({
        key: `conflict-${inv.id}`,
        severity: 'warning',
        text: `${name} · sources disagree`,
        help: 'Two source documents disagreed. The Notes tab says how.',
      });
    }
  }
  const c = detail.counts;
  if (c.invoicedLines > 0 && c.orderedLines > 0 && c.invoicedLines !== c.orderedLines) {
    issues.push({
      key: 'inv-linecount',
      severity: 'warning',
      text: `${c.invoicedLines} products invoiced vs ${c.orderedLines} ordered`,
      help: 'The Items tab lists the variances.',
    });
  }
  return issues;
}

/** A need and the blocker that says the same thing — one line, not two. */
const NEED_FOR_BLOCKER: Record<string, string> = { invoice_lineless: 'invoice_no_lines' };

export function receiptIssues(
  detail: OrderDetail,
  receipt: Receipt,
  state: OverviewReceiving | undefined
): Issue[] {
  const issues: Issue[] = [];
  if (!state || receipt.balancedAt) return issues;
  const n = state.undecidedDiscrepancies;
  if (n > 0) {
    issues.push({
      key: `disc-${receipt.id}`,
      // A count to settle needs you; it is not a failure (see lib/status.ts).
      severity: 'warning',
      text: `${n} count difference${n === 1 ? '' : 's'} to settle`,
      help: 'Decide each in Discrepancies: recount, credit, recapture, or let it go.',
    });
  }
  const needCodes = new Set(state.needs.map((need) => need.code as string));
  for (const need of state.needs) {
    issues.push({ key: `need-${receipt.id}-${need.code}`, severity: 'warning', text: need.text });
  }
  for (const b of state.blockers) {
    if (b.code === 'diff_settle' && n > 0) continue;
    if (needCodes.has(b.code) || needCodes.has(NEED_FOR_BLOCKER[b.code])) continue;
    issues.push({ key: `blk-${receipt.id}-${b.code}`, severity: 'warning', text: b.text, help: blockerHelp(b) });
  }
  // Only attributable with a single receiving; with several, the rows carry it.
  const reference = detail.counts.orderedLines || detail.counts.invoicedLines;
  if (detail.receipts.length === 1 && detail.counts.receivedLines > 0 && reference > 0
    && detail.counts.receivedLines !== reference) {
    issues.push({
      key: `rcv-linecount-${receipt.id}`,
      severity: 'warning',
      text: `${detail.counts.receivedLines} products received vs ${reference} ${detail.counts.orderedLines ? 'ordered' : 'invoiced'}`,
      help: 'The Items tab lists the variances.',
    });
  }
  return issues;
}
